import React from 'react';
import { CAPABILITIES } from '@/lib/types/authorization';
import { useCan } from '@/lib/hooks/useCan';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AppLayout } from '@/components/layout/AppLayout';
import { PatientRecordView } from '@/components/views/PatientRecordView';
import { PatientFormModal } from '@/components/modals/PatientFormModal';
import { PatientFormContent } from '@/components/forms/PatientFormContent';
import { patientsApi } from '@/lib/api/patients';
import { medplumResourcesApi } from '@/lib/api/medplum-resources';
import { vitalsApi } from '@/lib/api/vitals';
import type { VitalSigns } from '@/lib/types/consultation';
import { getErrorMessage } from '@/lib/api/client';
import { getStatusLabel, getStatusVariant, formatGenderDisplay } from '@/lib/types/patient';
import { useAuthStore } from '@/lib/stores/authStore';
import { useIsAyurveda } from '@/lib/hooks';
import { useDepartmentConfig } from '@/lib/hooks/useDepartmentConfig';
import { generateDiagnosticReportsPDF } from '@/lib/utils/pdfGenerator';
import { formatConclusionAsText } from '@/lib/utils/conclusionParser';
import { startConsultationForPatient } from '@/lib/utils/encounter';
import { caseSheetSummaryApi } from '@/lib/api/case-sheet-summary';
import { applyVisitEditToSummary } from '@/lib/utils/caseSheetFhirPayload';
import { createComponentLogger } from '@/lib/utils/logger';
import {
  VISIT_TABS,
  mergeVisitEntries,
  filterVisitsByTab,
  countMyVsOther,
  type VisitTab,
} from '@/lib/utils/patientVisits';

const logger = createComponentLogger('PatientRecordPage');

/**
 * PatientRecordPage Component
 *
 * Displays detailed information for a single patient.
 *
 * Features:
 * - Patient demographics display
 * - Contact information
 * - Emergency contact details
 * - Edit and delete actions
 * - Confirmation dialog for deletion
 * - Loading and error states
 * - Navigation breadcrumbs
 *
 * Future phases will add:
 * - Visit history
 * - Consultation records
 * - Case sheets
 * - Medical history
 *
 * @accessibility
 * - Proper heading hierarchy
 * - Keyboard navigation
 * - Screen reader support
 * - Confirmation dialog for destructive actions
 */
export function PatientRecordPage() {
  const canConsult = useCan(CAPABILITIES.CONSULTATION_START);
  const navigate = useNavigate();
  const { patientId } = useParams<{ patientId: string }>();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const role = user?.role;
  const canViewAllPatients = role === 'admin' || role === 'practitioner';
  const showDoctorFilter = role === 'admin';
  const departmentKey = useAuthStore((state) => state.departmentKey);
  const isAyurveda = useIsAyurveda();
  const { departmentConfig } = useDepartmentConfig();
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = React.useState(false);
  const [selectedReportId, setSelectedReportId] = React.useState<string | null>(null);
  const [isRefreshingSummary, setIsRefreshingSummary] = React.useState(false);

  // Tab state for role-based visit filtering
  const [activeTab, setActiveTab] = React.useState<VisitTab>(VISIT_TABS.MINE);
  const [selectedPractitionerId, setSelectedPractitionerId] = React.useState<string | undefined>(
    undefined
  );

  // Administrators and practitioners can switch visit scopes.
  const showTabs = canViewAllPatients;

  // Fetch patient data
  const {
    data: patientData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => patientsApi.getById(patientId!),
    enabled: !!patientId,
  });

  // Fetched once per patient — "My Visits" / "All Visits" / "Other Visits" are
  // derived views over this one list (see mergedReports below), not separate queries.
  const {
    data: patientReports,
    isLoading: isLoadingReports,
    isError: isErrorReports,
    error: reportsError,
  } = useQuery({
    queryKey: ['patientReports', patientId],
    queryFn: () =>
      medplumResourcesApi.listDiagnosticReportsByPatient(patientId!, {
        resolveNames: showDoctorFilter,
      }),
    enabled: !!patientId,
  });

  // Fetch Composition (AI SOAP) summaries in parallel — new consultations create Composition,
  // not DiagnosticReport, for the AI SOAP note.
  const { data: compositionReports } = useQuery({
    queryKey: ['patientCompositions', patientId],
    queryFn: () =>
      medplumResourcesApi.listCompositionsByPatient(patientId!, {
        resolveNames: showDoctorFilter,
      }),
    enabled: !!patientId,
  });

  // Fetch DocumentSource (documentSource data) summaries in parallel
  const { data: documentSourceReports } = useQuery({
    queryKey: ['patientDocumentSource', patientId],
    queryFn: () => medplumResourcesApi.listDocumentSourcesByPatient(patientId!),
    enabled: !!patientId,
  });

  // Fetch practitioners list for DMO dropdown
  const { data: practitionersData } = useQuery({
    queryKey: ['practitioners-for-filter'],
    queryFn: async () => {
      return medplumResourcesApi.listPractitioners({ count: 100 });
    },
    enabled: showDoctorFilter,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Build practitioner options for the administrator dropdown
  const practitionerOptions = React.useMemo(() => {
    if (!practitionersData?.practitioners) return [];
    return practitionersData.practitioners
      .filter((practitioner) => !!practitioner.id)
      .map((practitioner) => {
        const name = practitioner.name?.[0];
        const displayName = name
          ? `${name.given?.join(' ') || ''} ${name.family || ''}`.trim()
          : practitioner.id || 'Unknown';
        return { id: practitioner.id!, name: displayName };
      });
  }, [practitionersData]);

  // Handle tab change
  const handleTabChange = (tab: VisitTab) => {
    setActiveTab(tab);
    if (tab === VISIT_TABS.MINE) {
      setSelectedPractitionerId(undefined); // Clear practitioner filter when switching to My Visits
    }
  };

  // Handle practitioner filter change
  const handlePractitionerFilterChange = (value: string | undefined) => {
    setSelectedPractitionerId(value);
  };

  // Merge DiagnosticReport and Composition summaries into one timeline list —
  // the full set of the patient's visits, across every practitioner.
  const mergedReports = React.useMemo(
    () =>
      mergeVisitEntries(
        patientReports || [],
        compositionReports || [],
        documentSourceReports || []
      ),
    [patientReports, compositionReports, documentSourceReports]
  );

  // "My Visits" / "All Visits" / "Other Visits" are pure filters over mergedReports,
  // so the tabs and their counts can never drift out of sync with each other.
  const visibleReports = React.useMemo(
    () => filterVisitsByTab(mergedReports, activeTab, user?.id, selectedPractitionerId),
    [mergedReports, activeTab, user?.id, selectedPractitionerId]
  );

  const visitCounts = React.useMemo(
    () => countMyVsOther(mergedReports, user?.id),
    [mergedReports, user?.id]
  );

  // Determine tab labels based on user role, with live counts from the same data the lists use.
  const tabLabels = React.useMemo(() => {
    const myVisits = `My Visits (${visitCounts.mine})`;
    // DMO: "All Visits" includes everyone, including the DMO's own. Practitioner: "Other Visits" excludes their own.
    const otherVisits = showDoctorFilter
      ? `All Visits (${visitCounts.all})`
      : `Other Visits (${visitCounts.other})`;
    return { myVisits, otherVisits };
  }, [showDoctorFilter, visitCounts]);

  const {
    data: selectedReportDetail,
    isLoading: isLoadingReportDetail,
    isError: isErrorReportDetail,
    error: reportDetailError,
  } = useQuery({
    queryKey: ['diagnosticReport', selectedReportId],
    queryFn: () => medplumResourcesApi.getDiagnosticReport(selectedReportId!),
    enabled:
      !!selectedReportId &&
      mergedReports.find((r) => r.item.id === selectedReportId)?.source === 'diagnosticReport',
  });

  // Fetch Composition detail when the selected item is Composition-sourced
  const { data: selectedCompositionDetail, isLoading: isLoadingCompositionDetail } = useQuery({
    queryKey: ['compositionDetail', selectedReportId],
    queryFn: () => medplumResourcesApi.getCompositionDetail(selectedReportId!),
    enabled:
      !!selectedReportId &&
      mergedReports.find((r) => r.item.id === selectedReportId)?.source === 'composition',
  });

  // Fetch DocumentSource detail when the selected item is DocumentSource-sourced
  const selectedEntry = mergedReports.find((r) => r.item.id === selectedReportId);
  const isDocumentSourceSelected = selectedEntry?.source === 'DocumentSource';
  const selectedDocumentSourceEncounterId = isDocumentSourceSelected
    ? selectedEntry.item.encounterId
    : null;

  const { data: selectedDocumentSourceDetail, isLoading: isLoadingDocumentSourceDetail } = useQuery(
    {
      queryKey: ['documentSourceDetail', selectedReportId, selectedDocumentSourceEncounterId],
      queryFn: () => caseSheetSummaryApi.getFhirSummary(selectedDocumentSourceEncounterId!),
      enabled: isDocumentSourceSelected && !!selectedDocumentSourceEncounterId,
    }
  );
  // When the Summary tab is selected (no visit chosen), the right column (Dosha Profile,
  // Medications, Investigations) should show data from the most recent visit.
  // Prefer DiagnosticReport over Composition because its conclusion JSON includes dosha_profile.
  const mostRecentReportEntry = React.useMemo(() => {
    const sorted = [...visibleReports].sort((a, b) => {
      const dateA = a.item.issued ? new Date(a.item.issued).getTime() : 0;
      const dateB = b.item.issued ? new Date(b.item.issued).getTime() : 0;
      return dateB - dateA;
    });
    return sorted.find((r) => r.source === 'diagnosticReport') ?? sorted[0] ?? null;
  }, [visibleReports]);

  const { data: mostRecentReportDetail, isLoading: isLoadingMostRecent } = useQuery({
    queryKey: ['diagnosticReport', 'summary-fallback', mostRecentReportEntry?.item.id],
    queryFn: () => medplumResourcesApi.getDiagnosticReport(mostRecentReportEntry?.item.id ?? ''),
    enabled:
      !selectedReportId &&
      !!mostRecentReportEntry &&
      mostRecentReportEntry.source === 'diagnosticReport',
  });

  const { data: mostRecentCompositionDetail, isLoading: isLoadingMostRecentComp } = useQuery({
    queryKey: ['compositionDetail', 'summary-fallback', mostRecentReportEntry?.item.id],
    queryFn: () => medplumResourcesApi.getCompositionDetail(mostRecentReportEntry?.item.id ?? ''),
    enabled:
      !selectedReportId &&
      !!mostRecentReportEntry &&
      mostRecentReportEntry.source === 'composition',
  });

  // Resolve the raw encounter ID for whichever visit is currently shown (selected visit,
  // or the most recent one when the Summary tab is active) — used to fetch that visit's vitals.
  const selectedVisitEncounterId = React.useMemo(() => {
    if (!selectedReportId) {
      if (!mostRecentReportEntry) return null;
      // The summary already carries encounterId for both source types — use it
      // immediately instead of waiting on the (slower) detail fetch, which only
      // resolves a beat later and would otherwise show a false "no vitals" flash.
      return mostRecentReportEntry.item.encounterId || mostRecentReportDetail?.encounterId || null;
    }

    const selectedEntry = mergedReports.find((r) => r.item.id === selectedReportId);
    if (!selectedEntry) return null;

    return selectedEntry.item.encounterId || selectedReportDetail?.encounterId || null;
  }, [
    selectedReportId,
    mergedReports,
    mostRecentReportEntry,
    mostRecentReportDetail,
    selectedReportDetail,
  ]);

  const { data: selectedVisitVitals, isLoading: isLoadingVitals } = useQuery({
    queryKey: ['vitals', selectedVisitEncounterId],
    queryFn: () => vitalsApi.getVitalsForEncounter(selectedVisitEncounterId!),
    enabled: !!selectedVisitEncounterId,
    staleTime: 60000,
  });

  // Note: We do NOT auto-select the first report anymore.
  // Initial state is null which means "Summary" tab is selected by default.

  const handleSelectReport = React.useCallback((reportId: string) => {
    // Empty string means "Summary" tab - set to null
    setSelectedReportId(reportId || null);
  }, []);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => patientsApi.delete(patientId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'], exact: false });
      toast.success('Patient deleted successfully');
      navigate('/dashboard');
    },
    onError: (error) => {
      toast.error('Failed to delete patient', {
        description: getErrorMessage(error),
      });
    },
  });

  // Handle delete confirmation
  const handleDelete = async () => {
    await deleteMutation.mutateAsync();
    setShowDeleteDialog(false);
  };

  // Visit edit mutation — writes the in-place edited visit back to wherever that visit
  // lives, preserving every field the edit UI didn't touch:
  //  - DiagnosticReport: the conclusion JSON is replaced.
  //  - DocumentSource: the saved FHIR summary is merged with the edits and re-saved
  //    (the backend upserts the encounter's FHIR resources).
  //  - Composition: only the edited sections' narrative is rewritten.
  const visitEditMutation = useMutation({
    mutationFn: async ({ reportId, conclusion }: { reportId: string; conclusion: string }) => {
      const entry = mergedReports.find((r) => r.item.id === reportId);
      const edited = JSON.parse(conclusion) as Record<string, unknown>;

      if (entry?.source === 'composition') {
        const html: Record<string, string> = {};
        Object.entries(edited).forEach(([title, value]) => {
          if (typeof value === 'string') html[title] = value;
        });
        return medplumResourcesApi.updateComposition(reportId, html);
      }

      if (entry?.source === 'DocumentSource') {
        const encounterId = entry.item.encounterId;
        if (!patientId || !user?.id || !encounterId) {
          throw new Error('Missing patient, practitioner or encounter for this visit');
        }
        const original = (selectedDocumentSourceDetail ?? {}) as Record<string, unknown>;
        return caseSheetSummaryApi.saveSummaryToFhir({
          patientId,
          practitionerId: user.id,
          encounterId,
          payload: applyVisitEditToSummary(original, edited),
        });
      }

      // The editor tags medication rows with `original_index` for the DocumentSource
      // merge above; a plain conclusion has no use for it.
      const cleaned = Object.fromEntries(
        Object.entries(edited).map(([key, value]) => [
          key,
          Array.isArray(value)
            ? value.map((row) => {
                if (row && typeof row === 'object') {
                  const rest = { ...(row as Record<string, unknown>) };
                  delete rest.original_index;
                  return rest;
                }
                return row;
              })
            : value,
        ])
      );
      return medplumResourcesApi.updateDiagnosticReport(reportId, {
        conclusion: JSON.stringify(cleaned),
      });
    },
    onSuccess: () => {
      toast.success('Visit updated successfully');
      queryClient.invalidateQueries({ queryKey: ['diagnosticReport'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['patientReports'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['compositionDetail'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['documentSourceDetail'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['patientDocumentSource'], exact: false });
    },
    onError: (error) => {
      toast.error('Failed to update visit', {
        description: getErrorMessage(error),
      });
    },
  });

  const handleSaveVisitEdit = async (reportId: string, conclusionJson: string) => {
    await visitEditMutation.mutateAsync({ reportId, conclusion: conclusionJson });
  };

  // Vitals edit mutation — updates/creates the underlying Observation resources.
  const vitalsEditMutation = useMutation({
    mutationFn: ({ encounterId, vitals }: { encounterId: string; vitals: VitalSigns }) =>
      vitalsApi.updateVitals({
        patientId: patientId!,
        encounterId,
        vitals,
        practitionerId: user?.id,
      }),
    onSuccess: (_data, { encounterId }) => {
      toast.success('Vitals updated successfully');
      queryClient.invalidateQueries({ queryKey: ['vitals', encounterId] });
    },
    onError: (error) => {
      toast.error('Failed to update vitals', {
        description: getErrorMessage(error),
      });
    },
  });

  const handleSaveVitals = async (encounterId: string, vitals: VitalSigns) => {
    await vitalsEditMutation.mutateAsync({ encounterId, vitals });
  };

  // Handle refresh summary - triggers summary regeneration and refetches patient data
  const handleRefreshSummary = async () => {
    if (!patientId || isRefreshingSummary) return;

    setIsRefreshingSummary(true);
    try {
      await patientsApi.triggerSummaryGeneration(patientId);

      // Wait for backend to process (summary generation takes 5-30 seconds)
      // Poll for updated data every 3 seconds, up to 15 seconds
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 3000;

      while (attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        await queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
        attempts++;
      }
    } catch (error) {
      logger.error('Failed to refresh summary:', error);
      toast.error('Failed to refresh summary', { description: getErrorMessage(error) });
    } finally {
      setIsRefreshingSummary(false);
    }
  };

  const patient = patientData?.patient;

  // State for starting consultation
  const [isStartingConsultation, setIsStartingConsultation] = React.useState(false);

  // Handle start consultation
  // Check for existing encounter with SOAP notes first, create new one if needed
  const handleStartConsultation = async () => {
    if (!patientId) return;
    if (isStartingConsultation) return; // Prevent double-clicking

    const user = useAuthStore.getState().user;
    if (!user) {
      toast.error('User not authenticated');
      return;
    }

    toast.info('Preparing consultation...', { duration: 2000 });
    setIsStartingConsultation(true);

    try {
      // Check if there's already an encounter with SOAP notes from PA Kiosk
      const existingEncounterId = await medplumResourcesApi.getEncounterWithComposition(patientId);

      let encounterId: string;

      if (existingEncounterId) {
        // Reuse the existing encounter that has SOAP notes
        logger.log('Reusing existing encounter with SOAP notes:', existingEncounterId);
        encounterId = existingEncounterId;

        // Update the encounter status to 'in-progress'
        await medplumResourcesApi.updateEncounter(encounterId, { status: 'in-progress' });
      } else {
        // No existing encounter with SOAP notes — use BFF to create encounter + appointment atomically
        const result = await startConsultationForPatient(patientId);
        encounterId = result.encounterId;
      }

      toast.success('Ready for consultation');
      navigate(`/consultation/${encounterId}`);
    } catch {
      toast.error('Failed to start consultation. Please try again.');
    } finally {
      setIsStartingConsultation(false);
    }
  };

  // Handle copy entire patient + reports text to clipboard
  const handleCopy = async () => {
    if (!patient) {
      toast.error('Patient information not available to copy');
      return;
    }

    const copyPromise = (async () => {
      const lines: string[] = [];

      // Patient header
      lines.push('Patient Summary');
      lines.push(`Name: ${patient.name || ''}`);
      lines.push(`Patient ID: ${patient.identifier || ''}`);
      if (patient.dateOfBirth) lines.push(`Date of Birth: ${patient.dateOfBirth}`);
      lines.push(`Age/Gender: ${patient.age ?? ''}/${formatGenderDisplay(patient.gender)}`);
      lines.push('');

      // If there are reports, fetch full details and append — scoped to the
      // currently visible tab (My/All/Other Visits), not every visit ever fetched.
      const diagnosticReportsInView = visibleReports
        .filter((entry) => entry.source === 'diagnosticReport')
        .map((entry) => entry.item);

      if (diagnosticReportsInView.length > 0) {
        for (const r of diagnosticReportsInView) {
          try {
            const fullReport = await medplumResourcesApi.getDiagnosticReport(r.id);

            lines.push(`${fullReport.codeText || 'Consultation Report'}`);
            if (fullReport.issued)
              lines.push(`Date: ${new Date(fullReport.issued).toLocaleDateString()}`);
            if (fullReport.status) lines.push(`Status: ${fullReport.status}`);

            // Resolve practitioner name if available
            let practitionerName: string | undefined = undefined;
            if (fullReport.performerDisplay && fullReport.performerDisplay.length > 0) {
              practitionerName = fullReport.performerDisplay[0];
            } else if (fullReport.performerIds?.[0]) {
              try {
                const pid = fullReport.performerIds[0];
                const practitioner = await medplumResourcesApi.getPractitioner(pid);
                practitionerName =
                  practitioner.name?.[0]?.text ||
                  [practitioner.name?.[0]?.given?.join(' ') || '', practitioner.name?.[0]?.family]
                    .filter(Boolean)
                    .join(' ') ||
                  undefined;
              } catch {
                // Silently fail - practitioner name is optional
              }
            }

            if (practitionerName) lines.push(`Practitioner: ${practitionerName}`);

            if (fullReport.conclusion) {
              lines.push('');
              lines.push(formatConclusionAsText(fullReport.conclusion));
            }

            lines.push(''); // separator between reports
          } catch {
            // Silently fail - skip this report
          }
        }
      }

      const text = lines.join('\n');
      await navigator.clipboard.writeText(text);
      return text;
    })();

    toast.promise(copyPromise, {
      loading: 'Copying patient data...',
      success: 'Patient data copied to clipboard',
      error: 'Failed to copy patient data',
    });
  };

  // Handle download of consultation records (DiagnosticReport + Composition visits) as PDF
  const handleDownload = async () => {
    if (!visibleReports || visibleReports.length === 0) {
      toast.error('No consultation records available to download');
      return;
    }

    if (!patient) {
      toast.error('Patient information not available');
      return;
    }

    try {
      // Create a promise for the entire PDF generation process
      const pdfGenerationPromise = async () => {
        // Download every visit shown in the Consultation History list — both
        // DiagnosticReport-sourced and Composition-sourced (AI SOAP) visits —
        // not just the narrower DiagnosticReport-only subset.
        const reportsToDownload = visibleReports;

        if (reportsToDownload.length === 0) {
          throw new Error('No reports found');
        }

        const fetchVitals = async (encounterId?: string | null) => {
          if (!encounterId) return undefined;
          try {
            return await vitalsApi.getVitalsForEncounter(encounterId);
          } catch {
            return undefined;
          }
        };

        // Resolve practitioner name: prefer explicit display name; if missing,
        // try practitionerId and fetch the Practitioner resource to get a name.
        const resolvePractitionerName = async (
          explicit?: string,
          practitionerId?: string
        ): Promise<string | undefined> => {
          if (explicit) return explicit;
          if (!practitionerId) return undefined;
          try {
            const practitioner = await medplumResourcesApi.getPractitioner(practitionerId);
            return (
              practitioner.name?.[0]?.text ||
              [practitioner.name?.[0]?.given?.join(' ') || '', practitioner.name?.[0]?.family]
                .filter(Boolean)
                .join(' ') ||
              undefined
            );
          } catch {
            return undefined;
          }
        };

        // Get full details for all reports
        const reportDetails = await Promise.all(
          reportsToDownload.map(async ({ item, source }) => {
            try {
              if (source === 'diagnosticReport') {
                const fullReport = await medplumResourcesApi.getDiagnosticReport(item.id);
                return {
                  id: fullReport.id,
                  title: fullReport.codeText || 'Consultation Report',
                  issued: fullReport.issued || undefined,
                  conclusion: fullReport.conclusion || undefined,
                  status: fullReport.status || undefined,
                  patientName: patient.name,
                  patientId: patient.identifier,
                  practitionerName: await resolvePractitionerName(
                    fullReport.performerDisplay?.[0],
                    fullReport.performerIds?.[0]
                  ),
                  vitals: await fetchVitals(fullReport.encounterId),
                };
              }

              // Composition-sourced (AI SOAP) visit
              const compositionDetail = await medplumResourcesApi.getCompositionDetail(item.id);
              const conclusion = compositionDetail?.sections?.length
                ? compositionDetail.sections
                    .filter((s) => s.content && s.content.trim())
                    .map((s) => `${s.title}:\n${s.content}`)
                    .join('\n\n')
                : undefined;

              return {
                id: item.id,
                title: item.codeText || 'Consultation Report',
                issued: item.issued || undefined,
                conclusion,
                status: item.status || undefined,
                patientName: patient.name,
                patientId: patient.identifier,
                practitionerName: await resolvePractitionerName(
                  item.practitionerName,
                  item.practitionerId
                ),
                vitals: await fetchVitals(item.encounterId),
              };
            } catch {
              // Return basic info if full details fail
              return {
                id: item.id,
                title: item.codeText || 'Consultation Report',
                issued: item.issued || undefined,
                conclusion: undefined,
                status: item.status || undefined,
                patientName: patient.name,
                patientId: patient.identifier,
                practitionerName: undefined,
                vitals: await fetchVitals(item.encounterId),
              };
            }
          })
        );

        // Generate PDF with patient info
        await generateDiagnosticReportsPDF(reportDetails, {
          name: patient.name,
          id: patient.identifier,
          dateOfBirth: patient.dateOfBirth,
          age: patient.age,
          gender: formatGenderDisplay(patient.gender),
        });

        return reportDetails;
      };

      toast.promise(pdfGenerationPromise(), {
        loading: 'Generating PDF...',
        success: 'PDF downloaded successfully',
        error: 'Failed to generate PDF. Please try again later.',
      });
    } catch {
      // Error is already handled by toast.promise
    }
  };

  return (
    <AppLayout>
      <PatientRecordView
        patientId={patientId}
        header={{
          name: patient?.name || '',
          identifier: patient?.identifier || '',
          statusLabel: patient ? getStatusLabel(patient.status) : '',
          statusVariant: patient ? getStatusVariant(patient.status) : 'default',
        }}
        patient={{
          name: patient?.name || '',
          dateOfBirth: patient?.dateOfBirth || '',
          age: patient?.age || 0,
          gender: patient?.gender || 'U',
          maritalStatus: patient?.maritalStatus || null,
          identifier: patient?.identifier || '',
          createdAt: patient?.createdAt || '',
          phoneNumber: patient?.phoneNumber || null,
          email: patient?.email || null,
          address: patient?.address || null,
          city: patient?.city || null,
          state: patient?.state || null,
          postalCode: patient?.postalCode || null,
          country: patient?.country || null,
          emergencyContactName: patient?.emergencyContactName || null,
          emergencyContactPhone: patient?.emergencyContactPhone || null,
          visitSummary: patient?.visitSummary || null,
          visitSummaryLastUpdated: patient?.visitSummaryLastUpdated || null,
        }}
        actions={{
          onBack: () => navigate(-1),
          onEdit: () => setShowEditPatientModal(true),
          onSaveVisitEdit: handleSaveVisitEdit,
          onDelete: () => setShowDeleteDialog(true),
          onStartConsultation: canConsult ? handleStartConsultation : undefined,
          onCopy: handleCopy,
          onDownload: handleDownload,
          onRefreshSummary: handleRefreshSummary,
          onSaveVitals: handleSaveVitals,
        }}
        isRefreshingSummary={isRefreshingSummary}
        deleteConfirm={{
          open: showDeleteDialog,
          onOpenChange: setShowDeleteDialog,
          onConfirm: handleDelete,
          pending: deleteMutation.isPending,
        }}
        loading={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Unknown error') : null}
        isAyurveda={isAyurveda}
        departmentConfig={departmentConfig}
        departmentKey={departmentKey}
        tabs={
          showTabs
            ? {
                activeTab,
                onTabChange: handleTabChange,
                myVisitsLabel: tabLabels.myVisits,
                otherVisitsLabel: tabLabels.otherVisits,
                otherVisitsKey: showDoctorFilter ? VISIT_TABS.ALL : VISIT_TABS.OTHER,
              }
            : undefined
        }
        practitionerFilter={
          showDoctorFilter
            ? {
                value: selectedPractitionerId,
                onChange: handlePractitionerFilterChange,
                options: practitionerOptions,
              }
            : undefined
        }
        reports={{
          items:
            visibleReports.map(({ item, source }) => ({
              id: item.id,
              title: item.codeText,
              status: item.status,
              issued: item.issued || undefined,
              encounter: item.encounterDisplay || item.encounterId || null,
              source,
              // Only administrators see practitioner names in this view.
              practitionerName: showDoctorFilter ? item.practitionerName : undefined,
            })) || [],
          isLoading: isLoadingReports,
          error: isErrorReports ? getErrorMessage(reportsError) : null,
          selectedId: selectedReportId,
          onSelect: handleSelectReport,
          detail: {
            item: (() => {
              // Summary tab (no visit selected) — use most recent report for right column data
              if (!selectedReportId) {
                if (
                  mostRecentReportEntry?.source === 'diagnosticReport' &&
                  mostRecentReportDetail
                ) {
                  return {
                    id: mostRecentReportDetail.id,
                    title: mostRecentReportDetail.codeText,
                    status: mostRecentReportDetail.status,
                    issued: mostRecentReportDetail.issued || null,
                    effective: mostRecentReportDetail.effective || null,
                    encounter:
                      mostRecentReportDetail.encounterDisplay ||
                      mostRecentReportDetail.encounterId ||
                      null,
                    subject:
                      mostRecentReportDetail.subjectDisplay ||
                      mostRecentReportDetail.subjectId ||
                      null,
                    conclusion: mostRecentReportDetail.conclusion || null,
                    source: 'diagnosticReport' as const,
                    practitionerName: mostRecentReportDetail.performerDisplay?.[0] || undefined,
                  };
                }
                if (
                  mostRecentReportEntry?.source === 'composition' &&
                  mostRecentCompositionDetail
                ) {
                  return {
                    id: mostRecentReportEntry.item.id,
                    title: mostRecentReportEntry.item.codeText,
                    status: mostRecentReportEntry.item.status,
                    issued: mostRecentReportEntry.item.issued || null,
                    effective: null,
                    encounter:
                      mostRecentReportEntry.item.encounterDisplay ||
                      mostRecentReportEntry.item.encounterId ||
                      null,
                    subject: null,
                    conclusion: null,
                    source: 'composition' as const,
                    soapData: mostRecentCompositionDetail ?? undefined,
                    practitionerName: mostRecentReportEntry.item.practitionerName || undefined,
                  };
                }
                return undefined;
              }

              // Specific visit selected — existing logic unchanged
              const selectedEntry = mergedReports.find((r) => r.item.id === selectedReportId);
              if (!selectedEntry) return undefined;

              if (selectedEntry.source === 'composition') {
                // Only return the detail item if the composition data is loaded
                if (!selectedCompositionDetail) return undefined;
                return {
                  id: selectedEntry.item.id,
                  title: selectedEntry.item.codeText,
                  status: selectedEntry.item.status,
                  issued: selectedEntry.item.issued || null,
                  effective: null,
                  encounter:
                    selectedEntry.item.encounterDisplay || selectedEntry.item.encounterId || null,
                  subject: null,
                  conclusion: null,
                  source: 'composition' as const,
                  soapData: selectedCompositionDetail ?? undefined,
                  practitionerName: selectedEntry.item.practitionerName || undefined,
                };
              }

              if (selectedEntry.source === 'DocumentSource') {
                // Only return the detail item if the document source data is loaded
                if (!selectedDocumentSourceDetail) return undefined;
                return {
                  id: selectedEntry.item.id,
                  title: selectedEntry.item.codeText,
                  status: selectedEntry.item.status,
                  issued: selectedEntry.item.issued || null,
                  effective: null,
                  encounter:
                    selectedEntry.item.encounterDisplay || selectedEntry.item.encounterId || null,
                  subject: null,
                  // The raw FHIR summary has the same shape as a DiagnosticReport
                  // conclusion, and PatientRecordView derives every section from
                  // `conclusion` — pass it through so DocumentSource visits render.
                  conclusion: JSON.stringify(selectedDocumentSourceDetail),
                  source: 'DocumentSource' as const,
                  documentSourceData: selectedDocumentSourceDetail,
                  practitionerName: selectedEntry.item.practitionerName || undefined,
                };
              }

              return selectedReportDetail
                ? {
                    id: selectedReportDetail.id,
                    title: selectedReportDetail.codeText,
                    status: selectedReportDetail.status,
                    issued: selectedReportDetail.issued || null,
                    effective: selectedReportDetail.effective || null,
                    encounter:
                      selectedReportDetail.encounterDisplay ||
                      selectedReportDetail.encounterId ||
                      null,
                    subject:
                      selectedReportDetail.subjectDisplay || selectedReportDetail.subjectId || null,
                    conclusion: selectedReportDetail.conclusion || null,
                    source: 'diagnosticReport' as const,
                    practitionerName: selectedReportDetail.performerDisplay?.[0] || undefined,
                  }
                : undefined;
            })(),
            isLoading: !selectedReportId
              ? isLoadingMostRecent || isLoadingMostRecentComp
              : isLoadingReportDetail ||
                isLoadingCompositionDetail ||
                isLoadingDocumentSourceDetail,
            error: isErrorReportDetail ? getErrorMessage(reportDetailError) : null,
          },
        }}
        vitals={selectedVisitVitals}
        isLoadingVitals={isLoadingVitals}
        vitalsEncounterId={selectedVisitEncounterId}
      />

      <PatientFormModal
        isOpen={showEditPatientModal}
        onClose={() => setShowEditPatientModal(false)}
      >
        <PatientFormContent
          patientId={patientId}
          onSuccess={() => setShowEditPatientModal(false)}
          onCancel={() => setShowEditPatientModal(false)}
          isModal={true}
        />
      </PatientFormModal>
    </AppLayout>
  );
}
